/* eslint-disable react-refresh/only-export-components */
import type { KitchenStatus } from "../../api/monitors";

// Кръглата статус-иконка на ред — една и съща навсякъде: в „Маркиране"
// (редовете на масата и легендата „?"), на мониторите (трите изгледа) и в
// Конфигурация → Монитори. Кръг с часовник = незапочнато, кръг с полукръг
// вътре = в процес, плътен с клош (капак за сервиране) = сготвено, плътен
// със самолетче = издадено, плътен с отметка = сервирано, плътен с „×" =
// сторно/грешка. Пунктиран кръг с пауза = задържана поръчка — още не е в
// кухнята (само в „Маркиране": мониторите задържаната не я виждат).
// Задържаната и в процес са с бледа вътрешност в собствения си цвят;
// незапочнатото е празен кръг със стрелките, в същия сив цвят.
export type KitchenStatusIconKey =
  | "held"
  | "none"
  | "progress"
  | "ready"
  | "issued"
  | "served"
  | "danger";

// Цветът на всяка иконка (pos токените на „Маркиране") — един и същ в
// „Маркиране" и на мониторите: незапочнато — сиво, в процес — светло
// оранжево, сготвено — тъмно оранжево, издадено — небесно синьо, сервирано —
// зелено, задържана — акцентът на темата, сторно/грешка — червено.
export const KITCHEN_STATUS_ICON_INK: Record<KitchenStatusIconKey, string> = {
  held: "text-pos-accent",
  served: "text-pos-served",
  issued: "text-pos-issued",
  ready: "text-pos-ready",
  progress: "text-pos-progress",
  none: "text-pos-none",
  danger: "text-pos-danger-ink",
};

// Иконата на всеки кухненски статус ("0"…"3").
export const KITCHEN_STATUS_ICON_KEYS: Record<KitchenStatus, KitchenStatusIconKey> = {
  "0": "none",
  "1": "progress",
  "2": "ready",
  "3": "issued",
};

// Кръгът е в `currentColor` (класа на иконата или наследения цвят), знакът в
// него — в цвета от `glyphClassName`: бял в „Маркиране" (кръгът там е винаги
// наситен), `text-surface` там, където кръгът е в цвета на текста и в тъмна
// тема е светъл (монитори, конфигурацията). `className` дава и размера —
// `size-[1em]`, за да следва шрифта като antd иконите. `label` — името на
// статуса за екранен четец (мониторите); без него иконата е само декорация.
export const KitchenStatusIcon = ({
  statusKey,
  className = "size-[19px]",
  glyphClassName = "text-white",
  label,
}: {
  statusKey: KitchenStatusIconKey;
  className?: string;
  glyphClassName?: string;
  label?: string;
}) => (
  <svg
    viewBox="0 0 20 20"
    className={className}
    role={label ? "img" : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  >
    {/* Бледата вътрешност на задържаната и в процес — собственият цвят,
        прозрачен; незапочнатото е празно (само кръгът и стрелките) */}
    {(statusKey === "held" || statusKey === "progress") && (
      <circle cx="10" cy="10" r="7.75" fill="currentColor" fillOpacity="0.14" />
    )}
    {statusKey === "held" && (
      <>
        <circle
          cx="10"
          cy="10"
          r="7.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeDasharray="2.6 2.3"
        />
        {/* пауза — „чака" */}
        <rect x="7.3" y="6.6" width="1.7" height="6.8" rx="0.85" fill="currentColor" />
        <rect x="11" y="6.6" width="1.7" height="6.8" rx="0.85" fill="currentColor" />
      </>
    )}
    {statusKey === "none" && (
      <>
        <circle
          cx="10"
          cy="10"
          r="7.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        {/* часовник — стрелките на 12 и 3 */}
        <path
          d="M10 5.6 V10.4 H13.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
    {statusKey === "progress" && (
      <>
        <circle
          cx="10"
          cy="10"
          r="7.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        {/* дясната половина на вътрешния кръг — „наполовина готово" */}
        <path d="M10 4.9 A5.1 5.1 0 0 1 10 15.1 Z" fill="currentColor" />
      </>
    )}
    {statusKey !== "held" && statusKey !== "none" && statusKey !== "progress" && (
      <circle cx="10" cy="10" r="8.75" fill="currentColor" />
    )}
    <g className={glyphClassName}>
      {statusKey === "ready" && (
        <>
          <circle cx="10" cy="6.4" r="1.05" fill="currentColor" />
          <path d="M5 12.7 A5 5 0 0 1 15 12.7 Z" fill="currentColor" />
          <rect x="4.2" y="13.4" width="11.6" height="1.6" rx="0.8" fill="currentColor" />
        </>
      )}
      {statusKey === "issued" && (
        <path
          d="M14.8 5.2 L4.9 9.4 L8.6 10.9 L14.8 5.2 L10.1 14.9 L8.6 10.9"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.9"
          strokeLinejoin="round"
        />
      )}
      {statusKey === "served" && (
        <path
          d="M6.2 10.4 L8.9 13 L13.9 7.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {statusKey === "danger" && (
        <path
          d="M7 7 L13 13 M13 7 L7 13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </g>
  </svg>
);
